import { useState } from "react";
import { Pencil, Trash2, CalendarX, Plus, Check, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { Modal } from "@/components/ui/Modal";
import { apiFetch, useApi } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/cn";
import { Inhabitant, InhabitantsResponse } from "@/types/inhabitants";
import { InhabitantModal } from "./inhabitants/InhabitantModal";
import { DeleteInhabitantDialog } from "./inhabitants/DeleteInhabitantDialog";

const LOCATION_LABELS: Record<string, string> = {
  krasinskiego: "Krasińskiego",
  stanczyka: "Stańczyka",
  rydlowka: "Rydlówka",
};

function fmtDate(d: string | undefined) {
  if (!d) return "—";
  return d.slice(0, 10);
}

function fmtPln(n: number | string | undefined) {
  if (n == null || n === "") return "—";
  return `${Number(n).toLocaleString("pl")} PLN`;
}

export default function Inhabitants() {
  const [showAll, setShowAll] = useState(false);
  const [tick, setTick] = useState(0);
  const { data, loading, error } = useApi<InhabitantsResponse>(
    `/api/inhabitants?show_all=${showAll}`,
    [showAll, tick]
  );
  const { push } = useToast();

  const [editing, setEditing] = useState<Inhabitant | null | "new">(null);
  const [deleting, setDeleting] = useState<Inhabitant | null>(null);
  const [ending, setEnding] = useState<Inhabitant | null>(null);
  const [endingDate, setEndingDate] = useState<string>("");
  const [actionLoading, setActionLoading] = useState(false);

  function openEndContract(inhabitant: Inhabitant) {
    setEnding(inhabitant);
    setEndingDate(inhabitant.endDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
  }

  function refetch() { setTick((n) => n + 1); }

  async function hardDelete() {
    if (!deleting) return;
    setActionLoading(true);
    try {
      await apiFetch(`/api/inhabitants/${deleting.agreementId}`, { method: "DELETE" });
      push({ message: `Usunięto: ${deleting.name}`, tone: "success" });
      setDeleting(null);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    } finally {
      setActionLoading(false);
    }
  }

  async function endContract(inhabitant: Inhabitant, date: string) {
    setActionLoading(true);
    try {
      await apiFetch(`/api/inhabitants/${inhabitant.agreementId}`, {
        method: "PUT",
        body: JSON.stringify({
          agreementId: inhabitant.agreementId,
          name: inhabitant.name,
          room: inhabitant.room,
          location: inhabitant.location,
          fee: inhabitant.fee,
          advance_fee: inhabitant.advanceFee,
          no_of_inhabitants: inhabitant.noOfInhabitants,
          email: inhabitant.email,
          start_date: inhabitant.startDate,
          end_date: date,
          trash_separate: inhabitant.trashSeparate,
        }),
      });
      push({ message: `Zakończono umowę: ${inhabitant.name} (data: ${date})`, tone: "success" });
      setEnding(null);
      setDeleting(null);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    } finally {
      setActionLoading(false);
    }
  }

  const rows = data?.inhabitants ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-sm font-semibold flex-1">Mieszkańcy</h1>
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                 className="h-4 w-4 rounded border-border accent-accent cursor-pointer" />
          Pokaż nieaktywnych
        </label>
        <Button iconLeft={<Plus className="size-3.5" />} onClick={() => setEditing("new")}>
          Dodaj mieszkańca
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <p className="text-sm text-danger">{error.message}</p>
      ) : (
        <>
          {/* Desktop table */}
          <Card padding="none" className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="text-xs text-text-muted">
                <tr className="border-b border-border">
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="text-left font-medium px-4 py-2">ID</th>
                  <th className="text-left font-medium px-4 py-2">Imię i nazwisko</th>
                  <th className="text-left font-medium px-4 py-2">Pokój</th>
                  <th className="text-left font-medium px-4 py-2">Lokal</th>
                  <th className="text-right font-medium px-4 py-2">Czynsz</th>
                  <th className="text-right font-medium px-4 py-2">Zaliczka</th>
                  <th className="text-right font-medium px-4 py-2">Osoby</th>
                  <th className="text-left font-medium px-4 py-2">E-mail</th>
                  <th className="text-left font-medium px-4 py-2">Od</th>
                  <th className="text-left font-medium px-4 py-2">Do</th>
                  <th className="text-center font-medium px-4 py-2" title="Śmieci osobno">Ś.</th>
                  <th className="px-4 py-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={13} className="px-4 py-6 text-center text-xs text-text-muted">
                      Brak mieszkańców
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.agreementId} className={cn(
                    "border-b border-border last:border-0 hover:bg-bg-subtle/50 transition-colors",
                    !r.isActive && "opacity-60",
                  )}>
                    <td className="px-4 py-2">
                      <Badge tone={r.isActive ? "success" : "muted"}>
                        {r.isActive ? "Aktywny" : "Nieaktywny"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-text-muted">{r.agreementId}</td>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 text-xs">{r.room || "—"}</td>
                    <td className="px-4 py-2 text-xs">{LOCATION_LABELS[r.location] ?? r.location}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtPln(r.fee)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtPln(r.advanceFee)}</td>
                    <td className="px-4 py-2 text-right text-xs">{r.noOfInhabitants ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-text-muted max-w-[160px] truncate"
                        title={r.email}>{r.email || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{fmtDate(r.startDate)}</td>
                    <td className="px-4 py-2 font-mono text-xs">{fmtDate(r.endDate)}</td>
                    <td className="px-4 py-2 text-center">
                      {r.trashSeparate
                        ? <Check className="size-3.5 text-success inline" />
                        : <X className="size-3.5 text-text-subtle inline" />}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton label="Edytuj" onClick={() => setEditing(r)}>
                          <Pencil className="size-3.5" />
                        </IconButton>
                        {r.isActive && (
                          <IconButton label="Zakończ umowę" onClick={() => openEndContract(r)}>
                            <CalendarX className="size-3.5" />
                          </IconButton>
                        )}
                        <IconButton label="Usuń" onClick={() => setDeleting(r)}>
                          <Trash2 className="size-3.5" />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="lg:hidden flex flex-col gap-3">
            {rows.length === 0 && (
              <p className="text-xs text-text-muted text-center py-6">Brak mieszkańców</p>
            )}
            {rows.map((r) => (
              <Card key={r.agreementId} padding="default"
                    className={cn(!r.isActive && "opacity-60")}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-sm">{r.name}</p>
                    <p className="text-xs text-text-muted">
                      {LOCATION_LABELS[r.location] ?? r.location}{r.room ? ` · ${r.room}` : ""}
                    </p>
                  </div>
                  <Badge tone={r.isActive ? "success" : "muted"}>
                    {r.isActive ? "Aktywny" : "Nieaktywny"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
                  <div className="text-text-muted">ID umowy</div>
                  <div className="font-mono">{r.agreementId}</div>
                  <div className="text-text-muted">Czynsz</div>
                  <div className="font-mono">{fmtPln(r.fee)}</div>
                  <div className="text-text-muted">Zaliczka</div>
                  <div className="font-mono">{fmtPln(r.advanceFee)}</div>
                  <div className="text-text-muted">Osoby</div>
                  <div>{r.noOfInhabitants}</div>
                  <div className="text-text-muted">E-mail</div>
                  <div className="truncate">{r.email || "—"}</div>
                  <div className="text-text-muted">Okres</div>
                  <div className="font-mono">{fmtDate(r.startDate)} – {fmtDate(r.endDate)}</div>
                  <div className="text-text-muted">Śmieci osobno</div>
                  <div>{r.trashSeparate ? "Tak" : "Nie"}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="secondary" iconLeft={<Pencil className="size-3" />}
                          onClick={() => setEditing(r)}>
                    Edytuj
                  </Button>
                  {r.isActive && (
                    <Button size="sm" variant="secondary" iconLeft={<CalendarX className="size-3" />}
                            onClick={() => openEndContract(r)}>
                      Zakończ
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" iconLeft={<Trash2 className="size-3" />}
                          onClick={() => setDeleting(r)}>
                    Usuń
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Summary */}
      {!loading && data && (
        <p className="text-xs text-text-muted text-right">
          Aktywni: {data.active_inhabitants.length} · Nieaktywni: {data.inactive_inhabitants.length}
        </p>
      )}

      {/* Add / Edit modal */}
      {editing !== null && (
        <InhabitantModal
          inhabitant={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { refetch(); setEditing(null); }}
        />
      )}

      {/* End contract — date picker dialog */}
      {ending !== null && (
        <Modal
          open
          onClose={() => setEnding(null)}
          title="Zakończyć umowę?"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setEnding(null)} disabled={actionLoading}>Anuluj</Button>
              <Button loading={actionLoading} disabled={!endingDate}
                      onClick={() => endContract(ending, endingDate)}>
                Zakończ umowę
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm">
            <p>
              Ustawi datę zakończenia umowy dla <strong>{ending.name}</strong>.
              Mieszkaniec zostanie oznaczony jako nieaktywny od wybranej daty.
            </p>
            <div className="flex flex-col gap-1">
              <label htmlFor="end-date" className="text-xs text-text-muted font-medium">
                Data zakończenia
              </label>
              <div className="flex gap-2">
                <input
                  id="end-date"
                  type="date"
                  value={endingDate}
                  onChange={(e) => setEndingDate(e.target.value)}
                  className="flex-1 h-9 rounded border border-border bg-bg px-3 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
                />
                <Button size="sm" variant="secondary"
                        onClick={() => setEndingDate(new Date().toISOString().slice(0, 10))}>
                  Dzisiaj
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Hard delete confirmation */}
      {deleting !== null && (
        <DeleteInhabitantDialog
          inhabitant={deleting}
          loading={actionLoading}
          onClose={() => setDeleting(null)}
          onEndContract={() => { setDeleting(null); setEnding(deleting); }}
          onDelete={hardDelete}
        />
      )}
    </div>
  );
}
